import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useToast } from '@bonusly/design-system';
import { EventEntity } from '@bonusly/one-on-ones-entities';
import { useCancelEvent } from '@bonusly/one-on-ones-queries';
import { UserDetailEntity } from '@bonusly/user-entities';

import { QueryKey as GrowthQueryKey } from '../../../../../modules/growth/types/growth-query-key';
import { QueryKey } from '../../types';

interface DiscontinueModalProps {
	onClose: VoidFunction;
	isOpen: boolean;
	event: EventEntity;
	guest: UserDetailEntity;
}

export const DiscontinueModal: FC<DiscontinueModalProps> = ({ isOpen, onClose, event, guest }) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { t } = useTranslation(['one_on_ones', 'common']);

	const { mutate, isLoading } = useCancelEvent({
		onSuccess: () => {
			queryClient.invalidateQueries([QueryKey.CurrentOneOnOne]);
			queryClient.invalidateQueries([QueryKey.Meetings]);
			queryClient.invalidateQueries([QueryKey.Meeting]);
			queryClient.invalidateQueries([QueryKey.Event, event.id]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { eventId: event.id }]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { userId: guest.id }]);
			queryClient.invalidateQueries([QueryKey.TeamActivityDetails, guest.id]);
			queryClient.invalidateQueries({ queryKey: [GrowthQueryKey.UserEvents] });

			toast({
				status: 'success',
				title: t('one_on_ones:meeting.modal.discontinue.toast_success'),
			});
			onClose();
			navigate('/growth/meet');
		},
		onError: () => {
			toast({
				status: 'error',
				title: t('one_on_ones:meeting.modal.discontinue.toast_error'),
			});
			onClose();
		},
	});

	const handleCancel = () => {
		mutate(event);
	};

	return (
		<Modal autoFocus={false} isOpen={isOpen} onClose={onClose} scrollBehavior="outside" size="sm" isCentered>
			<ModalOverlay />

			<ModalContent aria-label={t('meeting.modal.discontinue.modal_aria_label')} tabIndex={0}>
				<Box as="form" onSubmit={onClose} aria-label={t('meeting.modal.discontinue.header')} tabIndex={0}>
					<ModalHeader>{t('meeting.modal.discontinue.header')}</ModalHeader>
					<ModalCloseButton size="lg" borderRadius="full" />

					<ModalBody px={5} pt={5} pb={0}>
						{t('meeting.modal.discontinue.content', {
							participantName: guest.displayName,
						})}
					</ModalBody>

					<ModalFooter p={5} justifyContent="space-between">
						<Button color="primary.fg" mr={3} isActive={isLoading} onClick={onClose} variant="ghost">
							{t('common:cancel')}
						</Button>

						<Button colorScheme="primary" isLoading={isLoading} onClick={handleCancel}>
							{t('common:discontinue')}
						</Button>
					</ModalFooter>
				</Box>
			</ModalContent>
		</Modal>
	);
};
